<template>
  <div class="haendlerkontakte">
    <div class="card">
      <div class="card-header">
        <div class="header-content">
          <div>
            <h5 class="card-title">Händlerkontakte</h5>
          </div>
          <p-button type="primary" @click="openCreateDialog">
            <i class="bi bi-plus-lg"></i> Kontakt anlegen
          </p-button>
        </div>
      </div>
      <div class="card-body">
        <div class="toolbar">
          <el-input v-model="search" clearable placeholder="Über alle Spalten suchen..." class="search-input">
            <i slot="prefix" class="bi bi-search"></i>
          </el-input>
        </div>

        <el-table
          :data="filteredContacts"
          v-loading="loading"
          stripe
          empty-text="Keine Händlerkontakte gefunden."
          header-row-class-name="text-primary"
          @row-click="openDetails"
        >
          <el-table-column prop="company" label="Firma" min-width="190" sortable>
            <template slot-scope="scope">
              <strong>{{ scope.row.company }}</strong>
              <small v-if="scope.row.name" class="contact-name">{{ scope.row.name }}</small>
            </template>
          </el-table-column>
          <el-table-column label="E-Mail" min-width="190">
            <template slot-scope="scope">
              <a v-if="scope.row.email" :href="'mailto:' + scope.row.email" @click.stop>{{ scope.row.email }}</a>
              <span v-else class="muted">-</span>
            </template>
          </el-table-column>
          <el-table-column label="Telefon" min-width="155">
            <template slot-scope="scope">
              <template v-if="scope.row.phoneNumbers.length">
                <a v-for="(phone, index) in scope.row.phoneNumbers" :key="phone + index" :href="'tel:' + phone" class="phone-link" @click.stop>{{ phone }}</a>
              </template>
              <span v-else class="muted">-</span>
            </template>
          </el-table-column>
          <el-table-column label="" width="150" align="right" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" title="Details anzeigen" @click.stop="openDetails(scope.row)">
                <i class="bi bi-eye"></i>
              </el-button>
              <el-button type="text" title="Bearbeiten" @click.stop="openEditDialog(scope.row)">
                <i class="bi bi-pencil"></i>
              </el-button>
              <el-button type="text" class="danger-action" title="Löschen" @click.stop="removeContact(scope.row)">
                <i class="bi bi-trash"></i>
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="formVisible" width="760px" @closed="resetForm">
      <el-form ref="contactForm" :model="form" :rules="formRules" label-position="top">
        <div class="form-grid">
          <el-form-item label="Firma" prop="company" class="full-width">
            <el-input v-model="form.company" maxlength="150" clearable></el-input>
          </el-form-item>
          <el-form-item label="Name" prop="name">
            <el-input v-model="form.name" maxlength="150" clearable></el-input>
          </el-form-item>
          <el-form-item label="E-Mail" prop="email">
            <el-input v-model="form.email" type="email" maxlength="180" clearable></el-input>
          </el-form-item>
          <el-form-item label="Telefonnummern" prop="phoneNumbers" class="full-width">
            <div v-for="(phone, index) in form.phoneNumbers" :key="index" class="phone-row">
              <el-input v-model="form.phoneNumbers[index]" maxlength="60" clearable></el-input>
              <el-button v-if="form.phoneNumbers.length > 1" type="text" title="Telefonnummer entfernen" @click="removePhone(index)"><i class="bi bi-dash-circle"></i></el-button>
              <el-button v-if="index === form.phoneNumbers.length - 1" type="text" title="Telefonnummer hinzufügen" @click="addPhone"><i class="bi bi-plus-circle"></i></el-button>
            </div>
          </el-form-item>
          <el-form-item label="Internetseite" prop="website" class="full-width">
            <el-input v-model="form.website" maxlength="240" clearable placeholder="https://..."></el-input>
          </el-form-item>
          <el-form-item label="Benutzername" prop="username">
            <el-input v-model="form.username" maxlength="150" clearable></el-input>
          </el-form-item>
          <el-form-item label="Passwort" prop="password">
            <el-input v-model="form.password" :type="formPasswordVisible ? 'text' : 'password'" maxlength="240">
              <el-button slot="append" title="Passwort anzeigen" @click="formPasswordVisible = !formPasswordVisible">
                <i :class="formPasswordVisible ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
              </el-button>
              <el-button slot="append" title="Passwort kopieren" @click="copyPassword(form.password)">
                <i class="bi bi-clipboard"></i>
              </el-button>
            </el-input>
          </el-form-item>
          <el-form-item label="Standort(e)" prop="locationID" class="full-width">
            <el-select v-model="form.locationID" multiple filterable collapse-tags placeholder="Standorte auswählen" class="location-select">
              <el-option v-for="location in locations" :key="location.key" :label="location.name" :value="location.key"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="Anmerkung" prop="note" class="full-width">
            <el-input v-model="form.note" type="textarea" :rows="4" maxlength="2000" show-word-limit></el-input>
          </el-form-item>
        </div>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="formVisible = false">Abbrechen</el-button>
        <el-button type="primary" :loading="saving" @click="saveContact">Speichern</el-button>
      </span>
    </el-dialog>

    <el-dialog title="Händlerkontakt" :visible.sync="detailsVisible" width="620px">
      <div v-if="selectedContact" class="details-grid">
        <div class="detail-item detail-wide"><span>Firma</span><strong>{{ selectedContact.company }}</strong></div>
        <div class="detail-item"><span>Name</span><strong>{{ selectedContact.name || '-' }}</strong></div>
        <div class="detail-item"><span>E-Mail</span><a v-if="selectedContact.email" :href="'mailto:' + selectedContact.email">{{ selectedContact.email }}</a><strong v-else>-</strong></div>
        <div class="detail-item detail-wide"><span>Telefonnummern</span><template v-if="selectedContact.phoneNumbers.length"><a v-for="(phone, index) in selectedContact.phoneNumbers" :key="phone + index" :href="'tel:' + phone" class="phone-link detail-phone" @click.stop>{{ phone }}</a></template><strong v-else>-</strong></div>
        <div class="detail-item detail-wide"><span>Internetseite</span><a v-if="selectedContact.website" :href="normaliseWebsite(selectedContact.website)" target="_blank" rel="noopener">{{ selectedContact.website }}</a><strong v-else>-</strong></div>
        <div class="detail-item"><span>Benutzername</span><strong>{{ selectedContact.username || '-' }}</strong></div>
        <div class="detail-item"><span>Passwort</span><div class="password-value"><strong>{{ selectedContact.password ? (detailsPasswordVisible ? selectedContact.password : '••••••••') : '-' }}</strong><template v-if="selectedContact.password"><el-button type="text" title="Passwort anzeigen" @click="detailsPasswordVisible = !detailsPasswordVisible"><i :class="detailsPasswordVisible ? 'bi bi-eye-slash' : 'bi bi-eye'"></i></el-button><el-button type="text" title="Passwort kopieren" @click="copyPassword(selectedContact.password)"><i class="bi bi-clipboard"></i></el-button></template></div></div>
        <div class="detail-item detail-wide"><span>Standorte</span><strong>{{ locationNames(selectedContact.locationID) }}</strong></div>
        <div class="detail-item detail-wide"><span>Anmerkung</span><p>{{ selectedContact.note || '-' }}</p></div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailsVisible = false">Schließen</el-button>
        <el-button type="primary" @click="openEditDialog(selectedContact)">Bearbeiten</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { db } from '../../../../firebaseDb'
import store from '../../../../store'
import locationStore from '../../../../locationStore'
import { Button, Dialog, Form, FormItem, Input, Loading, MessageBox, Option, Select, Table, TableColumn } from 'element-ui'
import Vue from 'vue'

Vue.use(Loading)

const emptyForm = () => ({
  company: '', name: '', email: '', phoneNumbers: [''], website: '', username: '', password: '', note: '', locationID: []
})

export default {
  components: {
    'p-button': Button,
    'el-dialog': Dialog,
    'el-form': Form,
    'el-form-item': FormItem,
    'el-input': Input,
    'el-option': Option,
    'el-select': Select,
    'el-table': Table,
    'el-table-column': TableColumn
  },
  data () {
    return {
      contacts: [], loading: true, saving: false, search: '', location: store.getters.userlocation,
      formVisible: false, detailsVisible: false, selectedContact: null, editingId: null, form: emptyForm(), formPasswordVisible: false, detailsPasswordVisible: false,
      locations: locationStore.getters.locations.locations || [],
      formRules: {
        company: [{ required: true, message: 'Bitte eine Firma eingeben.', trigger: 'blur' }],
        email: [{ validator: (rule, value, callback) => { if (!value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) callback(); else callback(new Error('Bitte eine gültige E-Mail-Adresse eingeben.')) }, trigger: 'blur' }]
      }, unsubscribe: null, unwatchLocation: null
    }
  },
  computed: {
    dialogTitle () { return this.editingId ? 'Händlerkontakt bearbeiten' : 'Händlerkontakt anlegen' },
    filteredContacts () {
      const search = this.search.trim().toLowerCase()
      return this.contacts.filter(contact => {
        if (!contact.locationID.includes(this.location)) return false
        if (!search) return true
        return [contact.company, contact.name, contact.email, contact.website, contact.username, contact.password, contact.note, contact.phoneNumbers.join(' '), this.locationNames(contact.locationID)].join(' ').toLowerCase().includes(search)
      })
    }
  },
  created () {
    this.loadContacts()
    this.unwatchLocation = store.watch(state => state.userlocation, location => { this.location = location; this.loadContacts() })
  },
  beforeDestroy () {
    if (this.unsubscribe) this.unsubscribe()
    if (this.unwatchLocation) this.unwatchLocation()
  },
  methods: {
    loadContacts () {
      if (this.unsubscribe) this.unsubscribe()
      this.loading = true
      this.location = store.getters.userlocation
      this.unsubscribe = db.collection('haendlerkontakte').orderBy('company').onSnapshot(snapshot => {
        this.contacts = snapshot.docs.map(doc => this.normaliseContact(doc.id, doc.data()))
        this.loading = false
      }, error => { this.loading = false; this.notify('Die Händlerkontakte konnten nicht geladen werden.', 'danger'); console.error(error) })
    },
    normaliseContact (id, data) {
      return { id, company: data.company || data.firma || '', name: data.name || '', email: data.email || '', phoneNumbers: Array.isArray(data.phoneNumbers) ? data.phoneNumbers : (data.phone ? [data.phone] : []), website: data.website || '', username: data.username || '', password: data.password || '', note: data.note || data.anmerkung || '', locationID: Array.isArray(data.locationID) ? data.locationID : [] }
    },
    locationNames (keys) {
      const names = (keys || []).map(key => { const location = this.locations.find(item => item.key === key); return location ? location.name : key }).filter(Boolean)
      return names.length ? names.join(', ') : 'Kein Standort'
    },
    openCreateDialog () {
      this.editingId = null; this.form = emptyForm(); this.form.locationID = this.locations.map(location => location.key); this.formPasswordVisible = false; this.formVisible = true
    },
    openEditDialog (contact) {
      this.detailsVisible = false; this.editingId = contact.id; this.form = { ...contact, phoneNumbers: contact.phoneNumbers.length ? [...contact.phoneNumbers] : [''], locationID: [...contact.locationID] }; this.formPasswordVisible = false; this.formVisible = true
    },
    openDetails (contact) { this.selectedContact = contact; this.detailsPasswordVisible = false; this.detailsVisible = true },
    resetForm () { this.form = emptyForm(); this.formPasswordVisible = false },
    addPhone () { this.form.phoneNumbers.push('') },
    removePhone (index) { if (this.form.phoneNumbers.length > 1) this.form.phoneNumbers.splice(index, 1) },
    saveContact () {
      this.$refs.contactForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        const payload = { ...this.form, company: this.form.company.trim(), phoneNumbers: this.form.phoneNumbers.map(phone => phone.trim()).filter(Boolean), locationID: [...this.form.locationID] }
        try {
          if (this.editingId) await db.collection('haendlerkontakte').doc(this.editingId).update(payload)
          else await db.collection('haendlerkontakte').add(payload)
          this.formVisible = false
          this.notify('Der Händlerkontakt wurde gespeichert.', 'success')
        } catch (error) { this.notify('Der Händlerkontakt konnte nicht gespeichert werden.', 'danger'); console.error(error) } finally { this.saving = false }
      })
    },
    async removeContact (contact) {
      try {
        await MessageBox.confirm('Soll der Kontakt „' + contact.company + '“ wirklich gelöscht werden?', 'Kontakt löschen', { type: 'warning', confirmButtonText: 'Löschen', cancelButtonText: 'Abbrechen' })
        await db.collection('haendlerkontakte').doc(contact.id).delete()
        this.notify('Der Händlerkontakt wurde gelöscht.', 'success')
      } catch (error) { if (error !== 'cancel' && error !== 'close') { this.notify('Der Händlerkontakt konnte nicht gelöscht werden.', 'danger'); console.error(error) } }
    },
    async copyPassword (password) {
      if (!password) return
      try { await navigator.clipboard.writeText(password); this.notify('Das Passwort wurde in die Zwischenablage kopiert.', 'success') } catch (error) { this.notify('Das Passwort konnte nicht kopiert werden.', 'danger'); console.error(error) }
    },
    notify (message, type) { this.$notify({ message, type }) },
    normaliseWebsite (website) { return /^https?:\/\//i.test(website) ? website : 'https://' + website }
  }
}
</script>

<style scoped>
.header-content, .toolbar, .password-value { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.card-subtitle { color: #777; margin: 4px 0 0; }
.toolbar { margin-bottom: 18px; justify-content: flex-start; }
.search-input { max-width: 420px; }
.contact-name, .muted { display: block; color: #888; }
.danger-action { color: #f56c6c; }
.form-grid, .details-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 20px; }
.full-width, .detail-wide { grid-column: 1 / -1; }
.location-select { width: 100%; }
.phone-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.phone-row .el-input { flex: 1; }
.phone-link { display: block; margin-bottom: 4px; }
.detail-phone { display: inline-block; margin-right: 16px; }
.detail-item { min-height: 64px; padding: 8px 0; border-bottom: 1px solid #eee; }
.detail-item span { display: block; color: #888; font-size: 12px; margin-bottom: 5px; }
.detail-item p { margin: 0; white-space: pre-wrap; }
.password-value { justify-content: flex-start; }
@media (max-width: 700px) {
  .header-content, .toolbar { align-items: stretch; flex-direction: column; }
  .search-input { max-width: none; width: 100%; }
  .form-grid, .details-grid { grid-template-columns: 1fr; }
  .full-width, .detail-wide { grid-column: auto; }
}
</style>